import React from "react";
import { Box, ButtonBase, Typography } from "@mui/material";
import { useAppSelector } from "../../store/hooks";
import { useGetPatientPlanQuery } from "../../pages/patient-dashboard/services/GetPatientPlanAPISlice";
import {
  getCurrentCarePlan,
  getPatientRoute,
  sortActivitiesByType,
} from "../../utility/functions/common-functions";
import CarePlanHeader from "./care-plan-widget-content/CarePlanHeader";
import CarePlanProgress from "./care-plan-widget-content/CarePlanProgress";
import CarePlanActivityList from "./care-plan-widget-content/CarePlanActivityList";
import CustomButton from "../../components/v2/custom-button/CustomButton";
// translation
import { useTranslation } from "react-i18next";
import { ArrowIcon } from "../../components/icon-components/ArrowIcon";
import DataLoader from "../../components/custom-data-loader/DataLoader";
import CarePlanWidgetSkeleton from "./care-plan-widget-content/CarePlanWidgetSkeleton";
import {
  ACTIVITY_COMPLETION_STATUS,
  WHITE,
} from "../../utility/constants/common-constants";
import { useNavigate } from "react-router-dom";

const CarePlanWidget: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    patientProfileInfo: { patientProfileId },
  } = useAppSelector((state) => state.patientProfileDetails);

  const patientPreferredLang = useAppSelector(
    (state) => state.patientOnboardingStatus.preferredLanguage
  );

  const { data, isLoading, error } = useGetPatientPlanQuery({
    patientId: patientProfileId,
    patientPreferredLang,
  });

  const goToCarePlanPage = () => {
    navigate(getPatientRoute(patientProfileId, "/care-plan"), {
      state: { selectedTabId: 2 },
    });
  };

  return (
    <DataLoader
      isLoading={isLoading}
      error={error}
      data={data}
      loadingComponent={<CarePlanWidgetSkeleton />}
      emptyComponent={<Typography>{t("noData")}</Typography>}
      errorComponent={
        <Typography color="error">{t("carePlan.noCurrentWeek")}</Typography>
      }
    >
      {() => {
        const currentPlan = getCurrentCarePlan(data);
        if (!currentPlan)
          return (
            <Typography
              variant="body2"
              textAlign="center"
              color="error"
            >
              {t("carePlan.noCurrentWeek")}
            </Typography>
          );

        const { displayGroupDisplayName, headerTextUnderGroup, activities } =
          currentPlan;

        const totalPoints = activities.reduce(
          (sum, a) => sum + a.activityPoints,
          0
        );

        const earnedPoints = activities
          .filter(
            (a) =>
              a.activityCompletionStatusCode ===
              ACTIVITY_COMPLETION_STATUS.COMPLETED
          )
          .reduce((sum, a) => sum + a.activityPoints, 0);

        const completedCount = activities.filter(
          (a) =>
            a.activityCompletionStatusCode ===
            ACTIVITY_COMPLETION_STATUS.COMPLETED
        ).length;

        const sortedActivities = sortActivitiesByType(activities);
        return (
          <Box
            p={{ md: 4, xs: "2rem 1rem" }}
            borderRadius={"8px 64px 16px 16px"}
            bgcolor={WHITE}
            width={"100%"}
            mx="auto"
          >
            <Box
              maxWidth={996}
              m="0 auto"
            >
              <ButtonBase onClick={goToCarePlanPage}>
                <Typography
                  variant="h3"
                  mb={2}
                >
                  {displayGroupDisplayName} {t("carePlan.carePlan")}{" "}
                  <Box component="span">
                    <ArrowIcon
                      width={20}
                      height={20}
                      style={{ transform: "rotate(180deg)" }}
                    />
                  </Box>
                </Typography>
              </ButtonBase>
              <CarePlanHeader description={headerTextUnderGroup} />
              <CarePlanProgress
                totalPoints={totalPoints}
                earnedPoints={earnedPoints}
                completedCount={completedCount}
                totalActivities={activities.length}
              />
              <CarePlanActivityList activities={sortedActivities} />

              <Box
                display="flex"
                justifyContent="center"
              >
                <CustomButton
                  variant="contained"
                  text={t("carePlan.viewAllWeeks")}
                  onClick={goToCarePlanPage}
                />
              </Box>
            </Box>
          </Box>
        );
      }}
    </DataLoader>
  );
};

export default CarePlanWidget;
